/**
 * StationBroadcasts — "My broadcasts" in the station Results tab (Stage 3.6, 1 Oct 2026).
 * One plain card per broadcast: name, recording date, public / private (changeable), and what was found
 * (model: activity; engine: sound families, dolphins, humpback) in words — no figures.
 */
import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { getSpeciesName } from '../../../data/speciesData';
import { sectionForSpecies } from '../../analyst/review/sections';
import '../../portal/portal-theme.css';

const FAMILY = { whistles: 'whistles', whistle: 'whistles', trains: 'echolocation clicks', clicks: 'clicks', bursts: 'burst pulses',
  pulsed: 'clicks', manatee: 'manatee calls', humpback: 'humpback sounds', baleen: 'baleen whale calls',
  baleen_mid: 'baleen whale calls (mid frequency)', lf_pulse: 'low-frequency pulses', sperm: 'sperm whale clicks' };
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmtDate = (d) => (d && !isNaN(d) ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }) + ' UTC' : '—');

// Two separate analyses, labelled (user 8 Oct 2026: "Calls in 0 %" next to the engine's findings was confusing):
// "Acoustic analysis" = the engine (engineSummary), "Detection model" = the pipeline's models (detectionSummary).
function heard(f) {
  const out = [];
  const es = f.engineSummary;
  const fams = Object.entries(es?.piecesByFamily || {}).filter(([, n]) => n > 0);
  if (es) {
    const pct = es.activityPercent != null ? `sounds in ${es.activityPercent}% of the recording` : '';
    const list = fams.map(([k, n]) => `${FAMILY[k] || k} (${n} × 10 s)`).join(', ');
    out.push(`Acoustic analysis: ${[pct, list].filter(Boolean).join(' — ') || 'no animal sounds found'}`);
  }
  const dp = es?.pulsed ?? (f.detectionSummary?.clickTrains !== undefined ? f.detectionSummary : null);
  // the animal by the broadcast's species (12 Oct 2026: a sperm whale file said "dolphin")
  const sp = (f.metadata?.species || [])[0];
  const animal = { sperm_whale: 'sperm whale', beaked_whale: 'beaked whale', porpoise_nbhf: 'porpoise' }[sectionForSpecies(sp, 'pulsed')]
    || (sp && sectionForSpecies(sp, 'pulsed') === 'delphinid_pulsed' ? 'dolphin' : 'animal');
  if (dp && (dp.animalsBest ?? 0) > 0) out.push(`Up to ${dp.animalsBest} ${animal}${dp.animalsBest === 1 ? '' : 's'} at once`);
  const hb = es?.branches?.humpback;
  if (hb && (hb.closeUnits || hb.distantUnits)) out.push(`Humpback: ${hb.closeUnits || 0} units from a close singer, ${hb.distantUnits || 0} from distant singers`);
  if (f.detectionSummary?.activityPercent != null) out.push(`Detection model: calls in ${f.detectionSummary.activityPercent}% of the recording`);
  if (es && f.detectionSummary?.activityPercent != null) out.push('The two are independent; they can disagree until the calls are reviewed.');
  return out;
}

function status(f) {
  if (f.engineStatus === 'running' || f.processingStatus === 'processing') return { t: '⏳ Being analysed', c: 'ct-muted' };
  if (f.engineStatus === 'error' || f.processingStatus === 'error') return { t: '⚠️ Analysis problem — we will look at it', c: 'ct-warn' };
  if (f.engineSummary || f.detectionSummary) return { t: '✅ Analysed', c: 'ct-msg' };
  return { t: '⏳ Waiting for analysis', c: 'ct-muted' };
}

const StationBroadcasts = ({ stationId }) => {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!stationId) return;
    getDocs(collection(db, 'stations', String(stationId), 'files')).then((snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((f) => f.s3KeyProxy || (f.displayName || '').toLowerCase().endsWith('.mp3'))
        .sort((a, b) => (toDate(b.uploadedAt)?.getTime() || 0) - (toDate(a.uploadedAt)?.getTime() || 0));
      setFiles(list);
    }).catch((e) => { setErr(e.message); setFiles([]); });
  }, [stationId]);

  const setPublic = async (f, isPublic) => {
    setBusy(f.id); setErr('');
    try {
      await updateDoc(doc(db, 'stations', String(stationId), 'files', f.id), { 'metadata.shareWithCommunity': isPublic });
      setFiles((l) => l.map((x) => (x.id === f.id ? { ...x, metadata: { ...(x.metadata || {}), shareWithCommunity: isPublic } } : x)));
    } catch (e) { setErr(`Could not change it: ${e.message}`); }
    setBusy(null);
  };

  return (
    <div style={{ margin: '24px 0' }}>
      <h3 className="ct-title">📻 My broadcasts</h3>
      <p className="ct-muted">What was found in each recording, and who can listen to it.</p>
      {err && <p className="ct-err">{err}</p>}
      {files === null ? <p className="ct-muted">Loading…</p> : files.length === 0 ? (
        <div className="ct-card"><p className="ct-muted" style={{ margin: 0 }}>No broadcasts yet — upload your first recording in the Upload tab.</p></div>
      ) : files.map((f) => {
        const isPublic = f.metadata?.shareWithCommunity !== false;
        const st = status(f);
        const lines = heard(f);
        const species = (f.metadata?.species || []).map((s) => getSpeciesName(s)).filter(Boolean).join(', ');
        return (
          <div key={f.id} className="ct-card">
            <div className="ct-row" style={{ justifyContent: 'space-between' }}>
              <div>
                <h3>{f.broadcastName || (f.displayName || '').replace(/\.(mp3|wav)$/i, '')}</h3>
                <div className="ct-muted">Recorded {fmtDate(toDate(f.recordingTimestamp || f.uploadedAt))}{species ? ` · ${species}` : ''}</div>
              </div>
              <div className="ct-row">
                <button className={`ct-btn ${isPublic ? 'on' : ''}`} disabled={busy === f.id} onClick={() => !isPublic && setPublic(f, true)}>🌍 Public</button>
                <button className={`ct-btn ${!isPublic ? 'on' : ''}`} disabled={busy === f.id} onClick={() => isPublic && setPublic(f, false)}>🔒 Private</button>
              </div>
            </div>
            <p className={st.c} style={{ margin: '10px 0 4px' }}>{st.t}</p>
            {lines.length > 0 && <ul style={{ margin: 0, paddingLeft: 18 }}>{lines.map((l) => <li key={l}>{l}</li>)}</ul>}
          </div>
        );
      })}
    </div>
  );
};

export default StationBroadcasts;
